import React, { useMemo, useState } from "react";
import NoData from "../common/NoData";
import AddSeasonModal from "./AddSeasonModal";
import DeleteSeasonMutation from "./mutations/DeleteSeason";
import { toast } from "react-toastify";
import EditSeasonModal from "./EditSeasonModal";

import PaginatedFlatTable from "../pagination/PaginatedFlatTable";
import { confirmDelete } from "../common/confirmDelete";
import useCheckLimitOrPrompt from "../common/hooks/useCheckLimitOrPrompt";
import useTranslation from "../../i18n/useTranslation"; // adjust path if needed
import { getLocalizedGraphQLError } from "../../util/graphqlError";

export default function SeasonList({ seasons, setSeasons, division, waivers }) {
    const { t } = useTranslation();
    const [deleteSeason] = DeleteSeasonMutation();
    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [seasonToEdit, setSeasonToEdit] = useState(null);

    const checkLimitOrPrompt = useCheckLimitOrPrompt();

    // ---- client-side pagination ----
    const pageSize = 25;
    const [page, setPage] = useState(1);

    const pages = useMemo(() => {
        const total = seasons?.length || 0;
        return Math.max(1, Math.ceil(total / pageSize));
    }, [seasons?.length]);

    const pagedSeasons = useMemo(() => {
        const list = Array.isArray(seasons) ? seasons : [];
        const start = (page - 1) * pageSize;
        return list.slice(start, start + pageSize);
    }, [seasons, page]);

    // keep page in range if seasons list shrinks
    React.useEffect(() => {
        if (page > pages) setPage(pages);
    }, [pages]); // eslint-disable-line react-hooks/exhaustive-deps

    const ui = useMemo(() => {
        return {
            chip: {
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "rgba(var(--kbs-primary-rgb, 0,173,255), 0.22)",
                color: "#14151a",
                fontWeight: 900,
                fontSize: 12,
                whiteSpace: "nowrap",
            },
            muted: { color: "rgba(20,21,26,0.60)", fontWeight: 700, fontSize: 12.5 },
            actionBtn: {
                width: 38,
                height: 38,
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
            },
            actionBtnDanger: {
                border: "1px solid rgba(220,53,69,0.35)",
                background: "rgba(220,53,69,0.06)",
                color: "#dc3545",
            },
            actionBtnEdit: {
                border: "1px solid rgba(0,0,0,0.10)",
                background: "rgba(0,0,0,0.02)",
                color: "#14151a",
            },
            emptyWrap: {
                borderRadius: 14,
                padding: 12,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.015)",
            },
        };
    }, []);

    // Open "Add Season", but gate by subscription limits
    const handleAddSeason = () => {
        const allowed = checkLimitOrPrompt({
            currentCount: seasons?.length || 0,
            itemLabel: "seasons",
            toast,
        });

        if (!allowed) return;
        setShowModal(true);
    };

    // actual mutation + state update
    const removeSeason = async (seasonId) => {
        try {
            const res = await deleteSeason({ variables: { seasonId } });

            const ok =
                res.data?.deleteDeason || // legacy typo safety
                res.data?.deleteSeason ||
                res.data?.delete_season;

            if (ok) {
                setSeasons((prev) => (prev || []).filter((s) => s.id !== seasonId));

                toast.success(t("seasonList.toasts.removeSuccess"), {
                    autoClose: 3000,
                    toastId: "remove_season_success",
                    position: "top-center",
                    closeOnClick: true,
                });
            } else {
                toast.error(t("seasonList.toasts.removeFailed"), {
                    autoClose: 3000,
                    toastId: "remove_season_failed",
                    position: "top-center",
                    closeOnClick: true,
                });
            }
        } catch (err) {
            toast.error(getLocalizedGraphQLError(err, t, "seasonList.toasts.removeFailed"), {
                autoClose: 3000,
                toastId: "season_delete_query_error",
                position: "top-center",
                closeOnClick: true,
            });
        }
    };

    const requestRemoveSeason = (season) => {
        confirmDelete({
            title: t("seasonList.deleteDialog.title"),
            message: t("seasonList.deleteDialog.message", { name: season.name }),
            confirmLabel: t("seasonList.deleteDialog.confirm"),
            cancelLabel: t("seasonList.deleteDialog.cancel"),
            danger: true,
            onConfirm: async () => {
                await removeSeason(season.id);
            },
        });
    };

    const editSeason = (season) => {
        setSeasonToEdit(season);
        setShowEditModal(true);
    };

    return (
        <>
            <div className="tab-pane" id="seasons">
                <div id="kbola-season-list">
                    {seasons?.length ? (
                        <PaginatedFlatTable
                            title={t("seasonList.title")}
                            subtitle={`${t("seasonList.subtitleManage")} ${division?.name || t("seasonList.thisDivision")}`}
                            page={page}
                            pages={pages}
                            onPageChange={(p) => setPage(p)}
                            items={pagedSeasons}
                            loading={false}
                            loadingOverlay={false}
                            onAdd={handleAddSeason}
                            headers={[
                                t("seasonList.headers.season"),
                                t("seasonList.headers.start"),
                                t("seasonList.headers.end"),
                                t("seasonList.headers.registrationStart"),
                                t("seasonList.headers.registrationEnd"),
                                t("seasonList.headers.actions"),
                            ]}
                            renderRow={(season) => {
                                const isDeletable =
                                    (season?.teams?.length || 0) === 0 && (season?.stages?.length || 0) === 0;

                                return (
                                    <tr key={season.slug || season.id}>
                                        <td>
                                            <div style={{ fontWeight: 900, color: "#14151a" }}>{season.name}</div>
                                            <div style={ui.muted}>
                                                <span style={ui.chip} title={t("seasonList.labels.teamsInSeason")}>
                                                    <i className="fas fa-users" />
                                                    {season?.teams?.length || 0}
                                                </span>
                                                <span style={{ display: "inline-block", width: 8 }} />
                                                <span style={ui.chip} title={t("seasonList.labels.stagesInSeason")}>
                                                    <i className="fas fa-boxes" />
                                                    {season?.stages?.length || 0}
                                                </span>
                                            </div>
                                        </td>

                                        <td>{season.startDate}</td>
                                        <td>{season.endDate}</td>
                                        <td>{season.registrationStartDate}</td>
                                        <td>{season.registrationEndDate}</td>

                                        <td className="text-right">
                                            <div style={{ display: "inline-flex", gap: 8 }}>
                                                <button
                                                    type="button"
                                                    style={{ ...ui.actionBtn, ...ui.actionBtnEdit }}
                                                    onClick={() => editSeason(season)}
                                                    title={t("seasonList.actions.editSeason")}
                                                >
                                                    <i className="fas fa-edit" />
                                                </button>

                                                {isDeletable ? (
                                                    <button
                                                        type="button"
                                                        style={{ ...ui.actionBtn, ...ui.actionBtnDanger }}
                                                        onClick={() => requestRemoveSeason(season)}
                                                        title={t("seasonList.actions.deleteSeason")}
                                                    >
                                                        <i className="fas fa-trash" />
                                                    </button>
                                                ) : null}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            }}
                        />
                    ) : (
                        <div style={ui.emptyWrap}>
                            <NoData />
                        </div>
                    )}
                </div>

                <style>{`
          #kbola-season-list i.fas,
          #kbola-season-list i.far,
          #kbola-season-list i.fal,
          #kbola-season-list i.fa,
          #kbola-season-list i[class^="fa-"],
          #kbola-season-list i[class*=" fa-"] {
            font-family: "Font Awesome 5 Free" !important;
            font-weight: 900;
          }
          #kbola-season-list i.fab {
            font-family: "Font Awesome 5 Brands" !important;
            font-weight: 400;
          }

          #kbola-season-list .kbola-table {
            min-width: 760px;
          }
        `}</style>
            </div>

            <AddSeasonModal
                show={showModal}
                onClose={() => setShowModal(false)}
                division={division}
                seasons={seasons}
                setSeasons={setSeasons}
                waivers={waivers}
            />

            <EditSeasonModal
                seasons={seasons}
                setSeasons={setSeasons}
                seasonToEdit={seasonToEdit}
                showModal={showEditModal}
                setShowModal={setShowEditModal}
                waivers={waivers}
                division={division}
            />
        </>
    );
}
